import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Play, Save, Check, Loader2, Download, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Script } from "@/hooks/useScripts";

interface EditorHeaderProps {
  title: string;
  setTitle: (title: string) => void;
  sceneType: string;
  setSceneType: (type: string) => void;
  status: "saving" | "saved" | "unsaved" | "error";
  isOffline: boolean;
  isPro: boolean;
  script: Script | null;
  isScriptCached: (id: string) => boolean;
  handleCacheScript: () => void;
  forceSave: () => void;
  handlePlay: () => void;
  // Stats
  wordCount: number;
  characterCount: number;
  charLimit: number | null;
  charUsage: number;
  isApproaching: boolean;
  isOver: boolean;
  formatLimit: (limit: number | null) => string;
}

export function EditorHeader({
  title,
  setTitle,
  sceneType,
  setSceneType,
  status,
  isOffline,
  isPro,
  script,
  isScriptCached,
  handleCacheScript,
  forceSave,
  handlePlay,
  wordCount,
  characterCount,
  charLimit,
  charUsage,
  isApproaching,
  isOver,
  formatLimit,
}: EditorHeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getSaveStatusBadge = () => {
    switch (status) {
      case "saving":
        return (
          <Badge variant="secondary" className="gap-1 h-7 text-xs px-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t("editor.saving")}
          </Badge>
        );
      case "saved":
        return (
          <Badge variant="success" className="gap-1 h-7 text-xs px-2">
            <Check className="h-3 w-3" />
            {t("editor.saved")}
          </Badge>
        );
      case "unsaved":
        return (
          <Badge variant="warning" className="gap-1 h-7 text-xs px-2">
            {t("editor.unsaved")}
          </Badge>
        );
      case "error":
        return (
          <Badge variant="destructive" className="gap-1 h-7 text-xs px-2">
            {t("editor.saveError")}
          </Badge>
        );
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-background border-b-2 border-foreground">
      {/* Mobile Header (< md) */}
      <div className="md:hidden">
        {/* Simplified Mobile Header: Back + Title + Scene + Status */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border/50">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="h-10 w-10 shrink-0 border-2 border-black rounded-lg bg-white hover:bg-[#FFD93D] transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>

          <div className="flex-1 min-w-0">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="h-10 text-sm font-medium border-2 border-black rounded-lg bg-white focus-visible:ring-2 focus-visible:ring-[#FF6B9D] focus-visible:ring-offset-0"
              placeholder={t("editor.untitled")}
            />
          </div>

          <Select value={sceneType} onValueChange={setSceneType}>
            <SelectTrigger className="w-[72px] h-10 text-xs border-2 border-black shrink-0 rounded-lg bg-white focus:ring-2 focus:ring-[#FF6B9D]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recording">{t("dashboard.sceneTypes.recording")}</SelectItem>
              <SelectItem value="speech">{t("dashboard.sceneTypes.speech")}</SelectItem>
              <SelectItem value="meeting">{t("dashboard.sceneTypes.meeting")}</SelectItem>
              <SelectItem value="podcast">{t("dashboard.sceneTypes.podcast")}</SelectItem>
            </SelectContent>
          </Select>

          {getSaveStatusBadge()}
        </div>
      </div>

      {/* Desktop Header (≥ md) */}
      <div className="hidden md:block">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Navigation + Title */}
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="h-10 w-10 border-2 border-black rounded-lg bg-white hover:bg-[#FFD93D] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[4px_4px_0_#1A1A1A] active:translate-x-1 active:translate-y-1 active:shadow-none transition-all duration-100"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>

              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-[200px] h-9 text-sm font-medium border-2 border-black"
                placeholder={t("editor.untitled")}
              />

              <Select value={sceneType} onValueChange={setSceneType}>
                <SelectTrigger className="w-[100px] h-9 text-xs border-2 border-black rounded-lg bg-white focus:ring-2 focus:ring-[#FF6B9D]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recording">{t("dashboard.sceneTypes.recording")}</SelectItem>
                  <SelectItem value="speech">{t("dashboard.sceneTypes.speech")}</SelectItem>
                  <SelectItem value="meeting">{t("dashboard.sceneTypes.meeting")}</SelectItem>
                  <SelectItem value="podcast">{t("dashboard.sceneTypes.podcast")}</SelectItem>
                </SelectContent>
              </Select>

              {/* Stats */}
              <div className="hidden lg:flex items-center gap-3 text-xs text-muted-foreground border-l border-border pl-3">
                <span>{t("editor.words")}: <span className="text-foreground font-medium">{wordCount.toLocaleString()}</span></span>
                <span className="flex items-center gap-1">
                  {t("editor.characters")}: 
                  <span className={isOver ? "text-red-500" : isApproaching ? "text-yellow-600" : "text-foreground font-medium"}>
                    {characterCount.toLocaleString()}/{formatLimit(charLimit)}
                  </span>
                  <Progress 
                    value={Math.min(charUsage, 100)} 
                    className={`h-1.5 w-16 ${isOver ? "[&>div]:bg-red-500" : isApproaching ? "[&>div]:bg-yellow-500" : ""}`}
                  />
                </span>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              {getSaveStatusBadge()}

              {script && isPro && !isOffline && (
                <Button 
                  variant={isScriptCached(script.id) ? "secondary" : "outline"} 
                  size="sm"
                  onClick={handleCacheScript}
                  className="gap-1 h-9 text-xs px-3 border-2 border-black bg-white hover:bg-gray-50 hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[4px_4px_0_#1A1A1A] active:translate-x-1 active:translate-y-1 active:shadow-none transition-all duration-100"
                >
                  {isScriptCached(script.id) ? (
                    <>
                      <Check className="h-4 w-4" />
                      {t("offline.cached")}
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      {t("offline.downloadOffline")}
                    </>
                  )}
                </Button>
              )}

              {isOffline && (
                <Badge variant="secondary" className="gap-1 h-9 text-xs px-3 border-2 border-black">
                  <WifiOff className="h-4 w-4" />
                  {t("offline.offlineMode")}
                </Badge>
              )}

              <Button 
                variant="outline" 
                size="sm" 
                onClick={forceSave} 
                disabled={isOffline} 
                className="h-9 gap-1 text-xs px-3 border-2 border-black bg-white hover:bg-gray-50 hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[4px_4px_0_#1A1A1A] active:translate-x-1 active:translate-y-1 active:shadow-none transition-all duration-100 whitespace-nowrap"
              >
                <Save className="h-4 w-4" />
                {t("editor.save")}
              </Button>

              <button
                onClick={handlePlay}
                className="
                  h-9 px-4 flex items-center gap-2 rounded-lg
                  bg-[#FF6B9D] text-black font-semibold text-sm
                  border-2 border-black whitespace-nowrap
                  hover:translate-x-0.5 hover:translate-y-0.5
                  hover:shadow-[4px_4px_0_#1A1A1A]
                  active:translate-x-1 active:translate-y-1 active:shadow-none
                  transition-all duration-100
                "
              >
                <Play className="h-4 w-4" />
                {t("editor.startTeleprompter")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
